import React, { useState, useEffect } from 'react';
import { FC } from 'react';
import { Icons } from '@components/Icon';
import { IconTemplate } from '@components/Icon/IconTemplate';
import useCart from '@hooks/useCart';
import useTranslations from '@hooks/useTranslation';
import ButtonTemplate from '@ui/Button/Button';
import ContentTemplate from '@ui/Content/Content';
import PackageTemplate from '@ui/Package/Package';
import Title from '@ui/SectionTitle/Title';
import { publicAssets } from '@utils/url.utils';
import { SUBSCRIPTION_PACKAGES } from '../../constants/packages';
import { useGTM } from '@hooks/useGTM';
import {
    Background,
    GarantImage,
    GarantLabel,
    GarantSection,
    Package,
    PackageDescriptionItem,
    PackageDescriptionList,
    PackageImage,
    PackageLabel,
    PackagePrice,
    PackagePriceSection,
    PackagePriceWithoutVat,
    PackageTitle,
    Packages,
} from './Packages.elements';

export interface PackagesProps { }

const PackagesTemplate: FC<PackagesProps> = () => {
    const { t } = useTranslations();

    const [loadingCart, setLoadingCart] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const { addOrUpdateItemInCart } = useCart();

    const { viewItem } = useGTM();

    useEffect(() => {
        if (SUBSCRIPTION_PACKAGES) {
            SUBSCRIPTION_PACKAGES?.forEach((item) => {
                const data = {
                    value: item?.price.replace(/,/g, '.'),
                    currency: 'EUR',
                    items: [{
                        item_id: item?.eso9,
                        item_name: item?.title,
                        item_brand: 'Profivzdelávanie',
                        price: item?.price.replace(/,/g, '.'),
                        quantity: 1,
                    }]
                }
                viewItem(data)
            });
        }
    }, [SUBSCRIPTION_PACKAGES]);

    const handleAddToCart = async (moduleId: any, key: string) => {
        try {
            setLoadingCart(true);
            setSelectedProduct(key);
            await addOrUpdateItemInCart(moduleId, 1)
            setLoadingCart(false);
            setSelectedProduct(null);
        } catch (error: any) {
            setLoadingCart(false);
            setSelectedProduct(null);
        }
    };

    return (
        <Background>
            <ContentTemplate mobileSidePadding={20}>
                <Title isBlack>{t('packages.title')}</Title>
                <Packages>
                    {SUBSCRIPTION_PACKAGES.map((subscriptionPackage, subscriptionPackageKey) => (
                        <Package key={subscriptionPackageKey}>
                            {subscriptionPackage?.garantLogo && (
                                <GarantSection>
                                    <GarantImage src={publicAssets(`/${subscriptionPackage.garantLogo}`)} />
                                    <GarantLabel>Odborný garant</GarantLabel>
                                </GarantSection>
                            )}
                            <PackageImage src={publicAssets(`/${subscriptionPackage.key}.png`)} />
                            <PackageTitle href={subscriptionPackage?.redirectLink}>
                                <PackageTemplate symbol={subscriptionPackage.icon} color={subscriptionPackage.color} />{' '}
                                {subscriptionPackage.title}
                            </PackageTitle>
                            <PackageLabel>{t('packages.label1')}</PackageLabel>
                            <PackageLabel>{t('packages.label2')}</PackageLabel>
                            <PackageDescriptionList>
                                {subscriptionPackage.benefits.map((benefit, benefitKey) => (
                                    <PackageDescriptionItem key={benefitKey}>
                                        <IconTemplate name={Icons.CheckThin} />
                                        <div dangerouslySetInnerHTML={{ __html: benefit }} />
                                    </PackageDescriptionItem>
                                ))}
                            </PackageDescriptionList>
                            <PackagePriceSection>
                                <PackagePrice>{subscriptionPackage.priceWithoutVat} € bez DPH / rok</PackagePrice>
                            </PackagePriceSection>
                            <PackagePriceSection>
                                <PackagePriceWithoutVat>
                                    {subscriptionPackage.price} € s DPH / rok
                                </PackagePriceWithoutVat>
                            </PackagePriceSection>
                            <ButtonTemplate
                                marginTop={20}
                                marginBottom={20}
                                text={t('packages.package.button')}
                                isLoading={loadingCart && selectedProduct == subscriptionPackage.key}
                                disabled={loadingCart && selectedProduct == subscriptionPackage.key}
                                onClick={() => handleAddToCart(subscriptionPackage.moduleId, subscriptionPackage.key)}
                            />
                        </Package>
                    ))}
                </Packages>
            </ContentTemplate>
        </Background>
    );
};

export default PackagesTemplate;
